import { useTranslation } from "Providers/TranslationProvider";
import { Moment } from "moment";
import { useCompanySetting } from "./hooks/useCompanySetting";
import { WorkingDaysKey } from "./types/WorkingDaysKey";

export const WORKING_DAYS_KEYS: WorkingDaysKey[] = ["MoFr", "MoSa", "MoSu"];

export function useTranslateWorkingDaysKey() {
	const { t } = useTranslation();

	return (workingDaysKey: WorkingDaysKey) => {
		switch (workingDaysKey) {
			case "MoFr":
				return t("WorkingDaysHelper_WorkingDaysKey_MoFr");
			case "MoSa":
				return t("WorkingDaysHelper_WorkingDaysKey_MoSa");
			case "MoSu":
				return t("WorkingDaysHelper_WorkingDaysKey_MoSu");
		}
	};
}

/**
 * ISO week day: `1 = Monday` to `7 = Sunday`.
 */
export function useIsIsoWeekdayEnabled() {
	const workingDays = useCompanySetting((state) => state.item?.workingDays);

	return (isoWeekday: number) => {
		const isEnabled = workingDays?.[isoWeekday - 1]?.enabled;

		return typeof isEnabled === "boolean" ? isEnabled : true;
	};
}

export function useEnabledWorkingDaysLength() {
	const workingDays = useCompanySetting((state) => state.item?.workingDays);

	if (workingDays) {
		return workingDays.reduce<number>(
			(result, workingDay) => result + (workingDay.enabled ? 1 : 0),
			0
		);
	} else {
		return 7;
	}
}

/**
 * Returns the amount of enabled days between to dates.
 * If a weekday is disabled, it leads to different results, e.g.
 *
 * ```
 * | S M T W T F S | Non | Sun | Sat + Sun |
 * +---------------+-----+-----+-----------+
 * | S-----------E |  7  |  6  |     5     |
 * | S---E         |  3  |  2  |     2     |
 * |         S---E |  3  |  3  |     2     |
 * +---------------+-----+-----+-----------+
 * ```
 */
export function useGetEnabledDaysDiff() {
	const isIsoWeekdayEnabled = useIsIsoWeekdayEnabled();

	return (startTime: Moment, endTime: Moment) => {
		const maxDaysLength = endTime.diff(startTime, "days");

		if (maxDaysLength > 0) {
			return Array(maxDaysLength)
				.fill(null)
				.reduce<number>((result, _, index) => {
					const isoWeekday = startTime.clone().add(index, "days").isoWeekday();

					if (isIsoWeekdayEnabled(isoWeekday)) {
						result++;
					}

					return result;
				}, 0);
		} else {
			return 0;
		}
	};
}
